// Welcome modal (Multi-user Stage B.4).
//
// Fires once when the authenticated user's `/me/profile` returns
// `welcomed_at === null`. Dismissal stamps `welcomed_at = now()` via
// PATCH /me/profile so the modal never appears again.
//
// Belt-and-braces: tracks a client-side localStorage flag too, so the
// modal doesn't flash briefly on subsequent loads while the server
// fetch is in flight.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { fetchProfile, markWelcomed } from "@/services/api/me";

const LOCAL_DISMISSED_KEY = "eryndal.welcome.dismissed";

const STARTER_QUERIES: string[] = [
  "What does NIS2 Article 21 require for incident response readiness?",
  "Map CIS Controls v8 to ISO 27001:2022 — top 5 gaps.",
  "What CVEs in the last 30 days affect Microsoft Exchange?",
];

export function WelcomeModal() {
  const queryClient = useQueryClient();

  // Skip the network fetch entirely if we already dismissed in this browser.
  const [hasLocalDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LOCAL_DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const { data, isLoading } = useQuery({
    queryKey: ["me", "profile"],
    queryFn: fetchProfile,
    enabled: !hasLocalDismissed,
    // No retry — if /me/profile is flaky, fail closed (skip the modal)
    // rather than showing a flickering welcome card.
    retry: false,
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: markWelcomed,
    onSuccess: (profile) => {
      queryClient.setQueryData(["me", "profile"], profile);
      try {
        localStorage.setItem(LOCAL_DISMISSED_KEY, "1");
      } catch {
        // ignore — server stamp is authoritative
      }
    },
  });

  useEffect(() => {
    if (data?.welcomed_at) {
      try {
        localStorage.setItem(LOCAL_DISMISSED_KEY, "1");
      } catch {
        // ignore
      }
    }
  }, [data?.welcomed_at]);

  if (hasLocalDismissed) return null;
  if (isLoading || !data) return null;
  if (data.welcomed_at) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-espresso-800/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
    >
      <div className="w-full max-w-lg border border-ruleline bg-parchment-50 px-8 py-10 shadow-xl">
        <p className="smallcaps text-oxblood-500">Welcome</p>
        <h2 id="welcome-title" className="mt-1 font-display text-3xl text-espresso-700">
          Eryndal is a compliance copilot.
        </h2>
        <p className="mt-4 font-serif text-warmgray-500">
          Ask about EU/UK regulations, security frameworks, vulnerabilities, and
          how they intersect. Citations + grounding scores on every answer.
        </p>

        <div className="mt-6">
          <p className="text-[0.65rem] uppercase tracking-wider text-warmgray-500">
            Try a starter question
          </p>
          <ul className="mt-2 space-y-1">
            {STARTER_QUERIES.map((q) => (
              <li
                key={q}
                className="font-serif text-sm italic text-espresso-700 before:mr-2 before:text-oxblood-500 before:content-['—']"
              >
                {q}
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-6 text-xs text-warmgray-500">
          Each account has a monthly spend cap. Yours is $25 by default — see
          live usage on the <span className="font-serif italic">Spend</span> tab.
        </p>

        <div className="mt-8 flex items-center justify-end">
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="border border-oxblood-500 bg-oxblood-500 px-6 py-2 font-serif text-parchment-50 transition-colors hover:bg-oxblood-600 disabled:cursor-wait disabled:opacity-60"
          >
            {mutation.isPending ? "Saving…" : "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
}
